// Percy tool names are MCP-prefixed (`mcp__<server>__<tool>`). The server prefix has
// changed over time as tools were re-homed between MCP servers, but persisted chat
// sessions still carry the old names — and a session can mix both. To stay backward
// compatible we match on a SET of accepted names per tool (legacy + current) rather
// than a single string. When a tool moves servers again, add the new name here and
// keep the old one; never replace.

// `create_artifact` now lives on the `artifacts` MCP server. It was historically
// matched as `mcp__footprint__create_artifact`, which never actually existed on the
// backend — kept here so any session that somehow carries it still resolves.
export const CREATE_ARTIFACT_TOOL_NAMES = [
  'mcp__artifacts__create_artifact',
  'mcp__footprint__create_artifact',
] as const;

// `gen_ui_block` now lives on the `ui_blocks` MCP server. Older sessions/fixtures
// carry the `footprint` and `meta` prefixes.
export const GEN_UI_BLOCK_TOOL_NAMES = [
  'mcp__ui_blocks__gen_ui_block',
  'mcp__footprint__gen_ui_block',
  'mcp__meta__gen_ui_block',
] as const;

export const isGenUiBlockTool = (toolName: string): boolean =>
  (GEN_UI_BLOCK_TOOL_NAMES as readonly string[]).includes(toolName);

// `get_agent_run` (run-agent polling) lives on the `meta` MCP server.
export const GET_AGENT_RUN_TOOL_NAMES = ['mcp__meta__get_agent_run'] as const;

export const isGetAgentRunTool = (toolName: string): boolean => {
  return (GET_AGENT_RUN_TOOL_NAMES as readonly string[]).includes(toolName);
};

export const RUN_AGENT_TOOL_NAME = 'mcp__meta__run_agent';

// `create_investigation` lives on the `meta` MCP server. Declared once and reused both as the
// resource-creation kind key below and by `isCreateInvestigationTool` (chat-message gates its
// "Investigation started" vs. error row on this).
export const CREATE_INVESTIGATION_TOOL_NAME = 'mcp__meta__create_investigation';
export const OPEN_IN_DASHBOARD_TOOL_NAME = 'mcp__meta__open_in_dashboard';

export const isCreateInvestigationTool = (toolName: string): boolean => {
  return toolName === CREATE_INVESTIGATION_TOOL_NAME;
};

// Tools whose entire output IS a "… created" resource card, mapped to the resource kind each
// produces. On success the card stands in for the tool, so chat-panel hides the tool's row (it's
// paired with the card by this kind); a FAILED call produces no card, so its row stays and
// surfaces the error — getToolDisplay still gives these a label for that case.
export const RESOURCE_CREATION_TOOL_OUTPUT_KIND: Record<string, string> = {
  mcp__meta__create_entity: 'entity',
  [RUN_AGENT_TOOL_NAME]: 'investigation',
  [CREATE_INVESTIGATION_TOOL_NAME]: 'investigation',
  mcp__meta__create_agent: 'agent',
  ...Object.fromEntries(CREATE_ARTIFACT_TOOL_NAMES.map(name => [name, 'artifact'])),
};

export const isResourceCreationTool = (toolName: string): boolean => {
  return toolName in RESOURCE_CREATION_TOOL_OUTPUT_KIND;
};
